<script setup lang="ts">
import { useField } from 'vee-validate'
import { computed, ref, watch } from 'vue'

const emit = defineEmits<(e: 'update:modelValue', value: string | number) => void>()

const props = defineProps({
  name: {
    type: String,
    required: false,
    default: '',
  },
  modelValue: {
    type: [String, Number],
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: 'text',
    validator(value: string) {
      return ['text', 'password', 'email', 'number'].includes(value)
    },
  },
  suffix: {
    type: String,
    default: '',
  },
  error: {
    type: String,
    default: '',
  },
  appearance: {
    type: String,
    default: '',
  },
  min: {
    type: Number,
    default: 0,
  },
  max: {
    type: Number,
    default: Infinity,
  },
  step: {
    type: Number,
    default: 1,
  },
})

const isFocused = ref(false)

const { value, errorMessage, handleBlur } = useField<string | number>(props.name, undefined, {
  initialValue: props.modelValue,
})

const hasError = computed(() => !!errorMessage.value)

const increment = () => {
  if (props.disabled) return

  const currentValue = Number(value.value) || 0
  const newValue = currentValue + props.step

  if (newValue <= props.max) {
    value.value = newValue
  }
}

const decrement = () => {
  if (props.disabled) return

  const currentValue = Number(value.value) || 0
  const newValue = currentValue - props.step

  if (newValue >= props.min) {
    value.value = newValue
  }
}

watch(value, (v) => {
  emit('update:modelValue', v as string | number)
})
</script>

<template>
    <div class="relative flex flex-col gap-1 w-full h-[71.47px]">
        <div class="flex items-start gap-2 w-full">
            <!-- Input -->
            <div class="relative flex-1 min-w-0">
                <!-- Label -->
                <label v-if="label" class="w-fit bg-white px-1 transition-all duration-400 z-50 pointer-events-none"
                    :class="[
                        (isFocused || value)
                            ? 'absolute ms-4 -mt-2 text-[10px] font-bold'
                            : 'absolute mt-[13px] ms-4 text-[13px] font-medium',
                        appearance === 'admin'
                            ? 'text-[#6C6C6C]'
                            : 'text-[#1b325a]',
                    ]">
                    {{ label }}
                </label>

                <!-- Input Container -->
                <div class="relative">
                    <input :name="name" :type="type" :disabled="disabled" :value="value" :placeholder="placeholder"
                        class="rounded-xl text-[#1A1A1A] bg-white border px-[18px] h-12 text-[13px] font-medium outline-none transition border-[#E0E0E0] focus:outline-none focus:ring-0 focus:border-black disabled:bg-gray-100 disabled:cursor-not-allowed w-full"
                        :class="[
                            suffix ? 'pe-16' : '',
                            hasError ? 'border-[#ed145b]' : '',
                        ]" @input="
                            value =
                            type === 'number'
                                ? ($event.target as HTMLInputElement).value === ''
                                    ? ''
                                    : Number(
                                        ($event.target as HTMLInputElement)
                                            .value
                                    )
                                : ($event.target as HTMLInputElement).value
                            " @focus="isFocused = true" @blur="
                                () => {
                                    isFocused = false;
                                    handleBlur();
                                }
                            " />

                    <!-- Suffix -->
                    <span v-if="suffix"
                        class="absolute end-4 top-1/2 -translate-y-1/2 text-[13px] font-medium text-[#1A1A1A] pointer-events-none"
                        :class="[
                            hasError
                                ? 'text-[#ed145b]'
                                : isFocused
                                    ? 'text-black'
                                    : 'text-[#1A1A1A]',
                        ]">
                        {{ suffix }}
                    </span>
                </div>

                <!-- Error -->
                <ErrorMessage v-if="name" :name="name" />
            </div>

            <!-- Counter -->
            <div class="h-12 flex items-center rounded-xl border border-[#E0E0E0] bg-white overflow-hidden shrink-0"
                :class="[
                    disabled
                        ? 'bg-gray-100 cursor-not-allowed opacity-60'
                        : '',
                ]">
                <!-- Plus -->
                <button type="button" :disabled="disabled || Number(value) >= max"
                    class="w-12 h-full flex items-center justify-center text-2xl cursor-pointer font-thin  text-[#1A1A1A] transition hover:bg-[#f8f9fa] disabled:cursor-not-allowed disabled:opacity-40"
                    @click="increment">
                    +
                </button>

                <!-- Divider -->
                <div class="h-full border-s border-[#E0E0E0]"></div>

                <!-- Minus -->
                <button type="button" :disabled="disabled || Number(value) <= min"
                    class="w-12 h-full flex items-center justify-center pt-2 cursor-pointer text-3xl font-thin  text-[#1A1A1A] transition hover:bg-[#f8f9fa] disabled:cursor-not-allowed disabled:opacity-40"
                    @click="decrement">
                    −
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Hide native number input arrows */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

input[type="number"] {
    -moz-appearance: textfield;
    appearance: textfield;
}
</style>